import { bindFunctions } from '@app/utils/class'
import { getRLContentContainer } from '@app/utils/window'
import { List } from 'immutable'
import * as React from 'react'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  IndexRange,
  InfiniteLoader,
  List as VirtualList,
  ListRowProps,
  WindowScroller,
} from 'react-virtualized'

const DEFAULT_ROW_HEIGHT = 40

interface IInfiniteScrollListProps {
  autoHeight?: boolean
  cacheKeyMapper?: (item: any, defaultKey: string) => any
  isLoading: boolean
  items: List<any>
  loadItems: (
    startIndex: number,
    stopIndex: number,
    append: boolean,
    loadComplete: () => void,
  ) => void
  minimumBatchSize?: number
  noRowsRenderer?: () => JSX.Element
  onRowClicked?: (item: any, index: number, event: any) => void
  overscanRowCount?: number
  rowHeight?: any
  rowRenderer: (item: any, index: number) => JSX.Element
  totalItems: number
}

export default class InfiniteScrollList extends React.PureComponent<
  IInfiniteScrollListProps
> {
  private cache?: CellMeasurerCache
  private loader: InfiniteLoader | null
  private reloadTimeoutId?: number
  private scrollElement?: Element
  private list: VirtualList | null

  constructor(props: IInfiniteScrollListProps) {
    super(props)

    bindFunctions(this)

    if (props.autoHeight === true) {
      this.cache = new CellMeasurerCache({
        defaultHeight: props.rowHeight || DEFAULT_ROW_HEIGHT,
        fixedWidth: true,
        keyMapper: this._cacheKeyMapper,
      })
    }
  }

  public componentDidMount() {
    this._loadItems(0, this.props.minimumBatchSize || 20, false)
  }

  public forceReloadData() {
    if (this.reloadTimeoutId) {
      clearTimeout(this.reloadTimeoutId)
    }

    this.reloadTimeoutId = window.setTimeout(() => {
      if (this.loader) {
        this.loader.resetLoadMoreRowsCache(true)
      }
      this._loadItems(0, this.props.minimumBatchSize || 20, false).then(() => {
        if (this.list) {
          this.list.forceUpdateGrid()
        }
      })
    }, 250)
  }

  public itemLayoutChanged(index?: number) {
    if (this.cache) {
      if (index === undefined) {
        this.cache.clearAll()
      } else {
        this.cache.clear(index, 0)
      }
    }
    if (this.list) {
      this.list.recomputeRowHeights(index)
    }
  }

  public recomputeRowHeights(index?: number) {
    if (this.list) {
      this.list.recomputeRowHeights(index)
    }
  }

  public render() {
    if (!this.scrollElement) {
      this.scrollElement = getRLContentContainer()
    }

    return (
      <WindowScroller
        scrollElement={this.scrollElement}
        onResize={this._onResize}
      >
        {({ height, isScrolling, onChildScroll, registerChild, scrollTop }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <div ref={registerChild as any}>
                <InfiniteLoader
                  isRowLoaded={this._isRowLoaded}
                  loadMoreRows={this._loadMoreRows}
                  minimumBatchSize={this.props.minimumBatchSize || 20}
                  ref={(el) => (this.loader = el)}
                  rowCount={this.props.totalItems}
                >
                  {({ onRowsRendered, registerChild: loaderChild }) => (
                    <VirtualList
                      autoHeight
                      className='mt-3'
                      deferredMeasurementCache={this.cache}
                      height={height || 0}
                      isScrolling={isScrolling}
                      noRowsRenderer={this.props.noRowsRenderer}
                      onRowsRendered={onRowsRendered}
                      onScroll={onChildScroll}
                      overscanRowCount={this.props.overscanRowCount || 10}
                      ref={(el) => {
                        this.list = el
                        loaderChild(el)
                      }}
                      rowCount={this.props.items.size}
                      rowHeight={
                        this.cache
                          ? this.cache.rowHeight
                          : this.props.rowHeight || DEFAULT_ROW_HEIGHT
                      }
                      rowRenderer={this._rowRenderer}
                      scrollTop={scrollTop}
                      width={width}
                    />
                  )}
                </InfiniteLoader>
              </div>
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    )
  }

  private _cacheKeyMapper(rowIndex: number, columnIndex: number) {
    const item = this._getItem(rowIndex)
    const defaultKey = `${rowIndex}-${columnIndex}`
    return this.props.cacheKeyMapper
      ? this.props.cacheKeyMapper(item, defaultKey)
      : defaultKey
  }

  private _getItem(index: number): any {
    return this.props.items.get(index)
  }

  private _isRowLoaded({ index }: Index): boolean {
    if (this.props.items.size <= index) {
      return false
    }
    return !!this._getItem(index)
  }

  private _loadItems(
    startIndex: number,
    stopIndex: number,
    append: boolean,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.props.loadItems(startIndex, stopIndex, append, resolve)
    })
  }

  private _loadMoreRows({ startIndex, stopIndex }: IndexRange): Promise<void> {
    if (this.props.isLoading) {
      return Promise.resolve()
    }

    return this._loadItems(startIndex, stopIndex, true)
  }

  private _onResize() {
    this.itemLayoutChanged()
  }

  private _onRowClicked(
    event: React.MouseEvent<HTMLDivElement>,
    item: any,
    index: number,
  ): void {
    if (this.props.onRowClicked) {
      this.props.onRowClicked(item, index, event)
    }
  }

  private _rowRenderer({
    index,
    key,
    parent,
    style,
  }: ListRowProps): JSX.Element {
    const item = this._getItem(index)

    if (!item) {
      return (
        <div key={key} style={style}>
          Loading {index}
        </div>
      )
    }

    if (this.props.autoHeight === true && this.cache) {
      const cacheKey = this._cacheKeyMapper(index, 0)
      return (
        <CellMeasurer
          cache={this.cache}
          columnIndex={0}
          key={cacheKey}
          parent={parent}
          rowIndex={index}
        >
          <div
            key={cacheKey}
            style={style}
            onClick={(e) => this._onRowClicked(e, item, index)}
          >
            {this.props.rowRenderer(item, index)}
          </div>
        </CellMeasurer>
      )
    } else {
      return (
        <div
          key={key}
          style={style}
          onClick={(e) => this._onRowClicked(e, item, index)}
        >
          {this.props.rowRenderer(item, index)}
        </div>
      )
    }
  }
}
